import { memo } from 'react';
import type {
  AiFluency as AiFluencyRecord,
  AiFluencyCompetency,
  AiFluencyEvidence,
} from '../../../../../fsm/jobs';
import styles from './index.module.css';

export interface AiFluencyProps {
  /** Rio's AI-fluency reading, written during the interview. Null or absent without one. */
  fluency?: AiFluencyRecord | null;
}

/** The four competencies, in the order a hiring manager reads them: what they hand over, how they ask, how they check, how they own it. */
const COMPETENCIES: {
  key: AiFluencyCompetency;
  name: string;
  about: string;
}[] = [
  {
    key: 'delegation',
    name: 'Delegation',
    about: 'What they hand to AI, and what they keep',
  },
  {
    key: 'description',
    name: 'Description',
    about: "How clearly they tell AI what's needed",
  },
  {
    key: 'discernment',
    name: 'Discernment',
    about: 'How they check what AI gives back',
  },
  { key: 'diligence', name: 'Diligence', about: 'How they own the result' },
];

/** Each level carries its own words, so it never relies on colour. */
const LEVELS: Record<string, { label: string; meaning: string; tone: string }> =
  {
    fluent: {
      label: 'Fluent',
      meaning: 'specific, repeated, with the reason',
      tone: 'fluent',
    },
    practised: {
      label: 'Practised',
      meaning: 'a specific time they did it',
      tone: 'practised',
    },
    aware: {
      label: 'Aware',
      meaning: 'names it, no specific time',
      tone: 'aware',
    },
    not_enough_evidence: {
      label: 'Not enough evidence',
      meaning: '',
      tone: 'none',
    },
  };

const EVIDENCE_LEVELS = new Set(['aware', 'practised', 'fluent', 'uncertain']);
const MAX_QUOTES = 3;

interface Row {
  key: AiFluencyCompetency;
  name: string;
  about: string;
  level: (typeof LEVELS)[string];
  quotes: { quote: string; uncertain: boolean }[];
}

/** The quotes as stored, trimmed, at most three; anything that is not a quote is dropped. */
const quotesOf = (evidence: unknown): Row['quotes'] =>
  (Array.isArray(evidence) ? evidence : [])
    .filter(
      (item): item is AiFluencyEvidence =>
        !!item &&
        typeof item === 'object' &&
        typeof (item as AiFluencyEvidence).quote === 'string' &&
        (item as AiFluencyEvidence).quote.trim() !== '' &&
        EVIDENCE_LEVELS.has((item as AiFluencyEvidence).level),
    )
    .slice(0, MAX_QUOTES)
    .map((item) => ({
      quote: item.quote.trim(),
      uncertain: item.level === 'uncertain',
    }));

/** The rows to show, in order; a competency with an unknown level is left out rather than shown wrong. */
const rowsOf = (fluency?: AiFluencyRecord | null): Row[] => {
  const competencies =
    fluency && typeof fluency.competencies === 'object' && fluency.competencies
      ? fluency.competencies
      : {};
  return COMPETENCIES.flatMap(({ key, name, about }) => {
    const reading = competencies[key];
    const level = reading ? LEVELS[reading.level] : undefined;
    if (!reading || !level) return [];
    return [{ key, name, about, level, quotes: quotesOf(reading.evidence) }];
  });
};

const doesNotUseAi = (fluency?: AiFluencyRecord | null): boolean =>
  fluency?.usesAi === false;

export const hasAiFluency = (fluency?: AiFluencyRecord | null): boolean =>
  doesNotUseAi(fluency) || rowsOf(fluency).length > 0;

/**
 * Rio's AI-fluency reading on the candidate page (Work Fluency phase 1): four
 * competencies from Anthropic's AI Fluency framework, each with a level and
 * the candidate's own words behind it. Rio writes the reading (avrio-rio
 * agents/interview/ai_fluency.py); this only lays it out. Evidence, not a
 * score: none of it is part of the fit score, and "not enough evidence" is
 * shown as plainly as a level, never as a failure.
 */
export const AiFluency = memo(({ fluency }: AiFluencyProps) => {
  if (!hasAiFluency(fluency)) return null;
  if (doesNotUseAi(fluency)) {
    return <p className={styles.noAi}>Doesn't use AI in their work.</p>;
  }
  return (
    <ul className={styles.rows}>
      {rowsOf(fluency).map((row) => (
        <li key={row.key} className={styles.row} aria-label={row.name}>
          <div className={styles.head}>
            <div className={styles.titles}>
              <span className={styles.name}>{row.name}</span>
              <span className={styles.about}>{row.about}</span>
            </div>
            <span className={`${styles.pill} ${styles[row.level.tone]}`}>
              {row.level.label}
              {row.level.meaning && (
                <span className={styles.meaning}>: {row.level.meaning}</span>
              )}
            </span>
          </div>
          {row.quotes.length > 0 && (
            <ul className={styles.quotes}>
              {row.quotes.map((quote, index) => (
                <li key={`${index}-${quote.quote}`} className={styles.quote}>
                  <q className={styles.quoteText}>{quote.quote}</q>
                  {quote.uncertain && (
                    <span className={styles.uncertain}>uncertain</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
});

AiFluency.displayName = 'AiFluency';

export default AiFluency;
